import React, { useState } from "react";
import PropTypes from "prop-types";

/**
 * Reusable Status Confirmation Modal component
 * Shows confirmation before booking status changes with special handling for driver-controlled bookings
 */
const StatusConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  booking,
  newStatus,
  isDriverControlled = false,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const handleConfirm = async () => {
    setIsProcessing(true);
    try {
      await onConfirm(booking._id, newStatus);
      onClose();
    } catch (error) {
      console.error("Error updating status:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const getStatusChangeMessage = () => {
    if (isDriverControlled) {
      const changedByText =
        booking.statusChangedBy === "driver"
          ? "driver"
          : booking.statusChangedBy === "admin"
            ? "admin"
            : "system";
      return `This booking status was already set to "${booking.status}" by the ${changedByText} and cannot be changed. Final statuses (completed/cancelled) are permanent.`;
    }

    return `Are you sure you want to change the booking status from "${booking.status}" to "${newStatus}"?`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "text-yellow-600";
      case "confirmed":
        return "text-blue-600";
      case "assigned":
        return "text-purple-600";
      case "inProgress":
        return "text-orange-600";
      case "completed":
        return "text-green-600";
      case "cancelled":
        return "text-red-600";
      default:
        return "text-gray-600";
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {isDriverControlled
              ? "Final Status Cannot Be Changed"
              : "Confirm Status Change"}
          </h3>

          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-3">
              <strong>Booking ID:</strong> {booking.bookingId || booking._id}
            </p>

            <p className="text-sm text-gray-700 mb-4">
              {getStatusChangeMessage()}
            </p>

            {!isDriverControlled && (
              <div className="flex items-center justify-between bg-gray-50 p-3 rounded-md">
                <div className="text-center">
                  <p className="text-xs text-gray-500">Current Status</p>
                  <p
                    className={`font-semibold capitalize ${getStatusColor(booking.status)}`}
                  >
                    {String(booking.status || "pending")}
                  </p>
                </div>

                <div className="text-gray-400">→</div>

                <div className="text-center">
                  <p className="text-xs text-gray-500">New Status</p>
                  <p
                    className={`font-semibold capitalize ${getStatusColor(newStatus)}`}
                  >
                    {newStatus}
                  </p>
                </div>
              </div>
            )}

            {isDriverControlled && (
              <div className="bg-red-50 border border-red-200 p-3 rounded-md">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-red-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-800">
                      Final statuses (completed/cancelled) cannot be modified
                      once set, regardless of who set them.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              disabled={isProcessing}
            >
              {isDriverControlled ? "Close" : "Cancel"}
            </button>

            {!isDriverControlled && (
              <button
                type="button"
                onClick={handleConfirm}
                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300"
                disabled={isProcessing}
              >
                {isProcessing ? "Updating..." : "Confirm Change"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

StatusConfirmationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  booking: PropTypes.object,
  newStatus: PropTypes.string,
  isDriverControlled: PropTypes.bool,
};

export default StatusConfirmationModal;
